import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	forwardRef,
	Fragment,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState
} from "react";
import { MoreInfo } from "../../MoreInfo/MoreInfo";
import { DialogContext, DialogContextData } from "../Dialog";
import classNames from "classnames";
import {
	ScrollableTabsNavigation,
	TabsNavigation,
	TabsNavigationProps,
} from "../../TabsNavigation/TabsNavigation";
import {
	MediaQueryKind,
	useCallbackWithoutArguments,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ButtonBase } from "../../ButtonBase/ButtonBase";
import { FontIconName } from "../../Icon/Icon";
import styles from "./DialogHeader.module.less";
import { Action, reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { DropOverActionList } from "../../DropOverActionList/DropOverActionList";
import { actionComponentFactory } from "../../ActionList/actionsRenderHelpers";
import { IconButtonLike } from "client/Components/IconButton/IconButtonLike";
import { IconButtonKind } from "client/Components/IconButton/IconButton";
import { localize } from "client_legacy/ndaw/culture";

export type NoTabsProps = {
	title: string | ReactElement;
	onClose?(): void;
	onShowInfo?(): void;
	onBack?(): void;
	hasBorder?: boolean;
	activeTab?: never;
	onTabClick?: never;
	tabs?: never;
};

export type WithTabsProps<TValue> = {
	title: string | ReactElement;
	onClose?(): void;
	onShowInfo?(): void;
	onBack?(): void;
	hasBorder?: boolean;
	tabs: TabsNavigationProps<TValue>["items"];
	activeTab: TValue;
	onTabClick: TabsNavigationProps<TValue>["onChange"];
};

export type WithMoreActionsProps<TContext> = {
	actionContext: TContext;
	actions: readonly Action<TContext>[];
};

export type WithNoActionsProps = {};

export type DialogHeaderProps<TValue, TContext> = (NoTabsProps | WithTabsProps<TValue>)
	& (WithMoreActionsProps<TContext> | WithNoActionsProps);

export const DialogHeader = <TValue, TContext>(
	props: DialogHeaderProps<TValue, TContext>
): ReactElement => {
	const {
		activeTab,
		title,
		onBack,
		onClose,
		onShowInfo,
		onTabClick,
		tabs = EMPTY_ARRAY,
		hasBorder = false
	} = props;

	const {
		isFullPageOnMobile,
		shouldAlwaysHaveSingleLineTitle
	} = useContext(DialogContext);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [maxRequiredWidthForTabsContainer, setMaxRequiredWidthForTabsContainer] = useState<number>();
	const [isMoreActionsOpen, setIsMoreActionsOpen] = useState<boolean>(false);

	const moreActionsRef: RefObject<HTMLElement | null> = useRef<HTMLElement>(null);

	const handleTabClick = useCallback(
		(newValue: TValue): void => {
			onTabClick?.(newValue);
		},
		[onTabClick]
	);

	const shouldRenderSimplifiedTitle: boolean = isMobile && isFullPageOnMobile;
	const shouldRenderTabsOnTheSameLine: boolean = !isMobile && tabs.length > 0;

	const moreActionsProps: WithMoreActionsProps<TContext> | { actions: undefined; actionContext: undefined; } = hasMoreActions(props)
		? {
			actions: props.actions,
			actionContext: props.actionContext
		}
		: { actions: undefined, actionContext: undefined };

	const moreActions = useMemo(
		(): WithMoreActionsProps<TContext> | undefined => {
			if (!moreActionsProps.actions) {
				return undefined;
			}

			return {
				actionContext: moreActionsProps.actionContext,
				actions: reduceToAvailableActions(
					moreActionsProps.actions,
					moreActionsProps.actionContext
				)
			};
		},
		[moreActionsProps.actions, moreActionsProps.actionContext]
	);

	return (
		<div>
			<div className={classNames(
				styles.root,
				{
					[styles.root_hasBorder]: hasBorder,
					[styles.root_hasFixedHeightOnMobile]: isFullPageOnMobile
				}
			)}>
				<div className={styles.leftPart}>
					{onBack && (
						<HeaderButton
							kind="secondary"
							position="left"
							icon="chevron-left"
							onClick={onBack}
							trackingId="dialog-back"
						/>
					)}
					{!shouldRenderSimplifiedTitle && (
						<h2 className={
							classNames(
								styles.title,
								{
									[styles.title_singleLine]: tabs.length > 0 || shouldAlwaysHaveSingleLineTitle
								}
							)
						}>
							<span className={styles.titleText}>
								{title}
							</span>
							{onShowInfo && (
								<span className={styles.info}>
									<MoreInfo
										{...setTrackingId("dialog-info")}
										onClick={onShowInfo}
									/>
								</span>
							)}
						</h2>
					)}
				</div>
				{(shouldRenderSimplifiedTitle || shouldRenderTabsOnTheSameLine) && (
					<div
						className={styles.middlePart}
						style={{
						flexBasis: shouldRenderTabsOnTheSameLine && maxRequiredWidthForTabsContainer
							// NOTE +1 px is added to account for fractional issues which we have with flex-basis
							? maxRequiredWidthForTabsContainer + 1
							: undefined
					}}
					>
						{shouldRenderSimplifiedTitle && (
							<h4 className={styles.title}>
								<span className={styles.titleText}>{title}</span>
							</h4>
						)}
						{shouldRenderTabsOnTheSameLine && (
							<div className={styles.tabsContainer}>
								<TabsNavigation
									height="fill"
									value={activeTab}
									onChange={handleTabClick}
									items={tabs}
									hasUnstableContainer={isNil(maxRequiredWidthForTabsContainer)}
									onMaxContentWidthChange={setMaxRequiredWidthForTabsContainer}
								/>
							</div>
						)}
					</div>
				)}
				<div className={styles.rightPart}>
					{moreActions && moreActions.actions.length > 0 && (
						<>
							<HeaderButton
								kind="transparent"
								position="right"
								icon="ellipsis"
								trackingId="dialog-more"
								onClick={() => setIsMoreActionsOpen(true)}
								ref={moreActionsRef}
							/>

							{moreActionsRef.current && isMoreActionsOpen && (
								<DropOverActionList
									trackingId={generateTrackingId("dialog-more", "__dropover")}
									target={moreActionsRef.current}
									targetAlignment="br"
									contentAlignment="tr"
									onClose={() => setIsMoreActionsOpen(false)}
									bubbleClick={false}
									menuTitle={localize("_Actions")}
								>
									{moreActions.actions.map((x): ReactElement => (
										<Fragment key={x.name}>
											{actionComponentFactory(x, moreActions.actionContext)}
										</Fragment>
									))}
								</DropOverActionList>
							)}
						</>
					)}
					<ExpandableButton/>
					{onShowInfo && shouldRenderSimplifiedTitle && (
						<HeaderButton
							kind="transparent"
							position="right"
							icon="circle-info"
							trackingId="dialog-info"
							onClick={onShowInfo}
						/>
					)}
					{onClose && (
						<HeaderButton
							kind="secondary"
							position="right"
							icon="xmark"
							onClick={onClose}
							trackingId="dialog-close"
						/>
					)}
				</div>
			</div>
			{isMobile && tabs.length > 0 && (
				<div className={styles.mobileTabsContainer}>
					<ScrollableTabsNavigation
						items={tabs}
						value={activeTab}
						onChange={handleTabClick}
					/>
				</div>
			)}
		</div>
	);
};

DialogHeader.displayName = "Dialog.Header";

const ExpandableButton = (): ReactElement | null => {
	const data: DialogContextData = useContext(DialogContext);

	if (!data.isEnlargeable) {
		return null;
	}

	const { isEnlarged, setIsEnlarged } = data;

	return (
		<HeaderButton
			kind="transparent"
			position="right"
			icon={isEnlarged
				? "arrow-down-left-and-arrow-up-right-to-center"
				: "arrow-up-right-and-arrow-down-left-from-center"
			}
			onClick={() => setIsEnlarged(!isEnlarged)}
			trackingId={isEnlarged ? "dialog-shrink" : "dialog-enlarge"}
			isDesktopOnly={data.isFullPageOnMobile}
		/>
	);
};
ExpandableButton.displayName = "ExpandableButton";

type HeaderButtonPosition = "left" | "right";

const buttonPositionToStyles: { [key in HeaderButtonPosition]: string | undefined } = {
	"left": styles.button_left,
	"right": styles.button_right
};

type HeaderButtonProps = {
	icon: FontIconName;
	kind: IconButtonKind;
	position: HeaderButtonPosition;
	isDesktopOnly?: boolean;
	onClick(): void;
	trackingId: string;
};

const HeaderButton = forwardRef(
	(props: HeaderButtonProps, ref: Ref<HTMLElement>): ReactElement => {
		const {
			icon,
			kind,
			position,
			isDesktopOnly = false,
			onClick,
			trackingId
		} = props;

		const [isBeingHovered, setIsBeingHovered] = useState<boolean>(false);

		const clickHandler: () => void = useCallbackWithoutArguments(onClick);

		return (
			<ButtonBase
				trackingId={trackingId}
				onClick={clickHandler}
				className={classNames(
					styles.button,
					{ [styles.button_desktopOnly]: isDesktopOnly },
					buttonPositionToStyles[position]
				)}
				onMouseEnter={() => setIsBeingHovered(true)}
				onMouseLeave={() => setIsBeingHovered(false)}
			>
				<IconButtonLike
					kind={kind}
					trackingId={generateTrackingId(trackingId, "__render")}
					ref={ref}
					icon={icon}
					shape="round"
					isActive={isBeingHovered}
				/>
			</ButtonBase>
		);
	}
);

HeaderButton.displayName = "HeaderButton";

function hasMoreActions<TContext>(props: WithMoreActionsProps<TContext> | WithNoActionsProps): props is WithMoreActionsProps<TContext> {
	return isNotNil((props as WithMoreActionsProps<TContext>).actions);
}
